import { useState, useEffect } from 'react';
import { User } from '../types';
import { RepositoryService } from '../services/RepositoryService';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const { withLoading } = useLoading();

  // Observer les changements d'état d'authentification
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const unsubscribe = RepositoryService.auth.onAuthStateChanged((user) => {
      // Ajouter un délai minimum de 500ms pour éviter le flash
      timeoutId = setTimeout(() => {
        setUser(user);
        setIsAuthenticated(!!user);
        setIsLoading(false);
      }, 500);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      const user = await withLoading(
        () => RepositoryService.auth.login({ email, password }),
        'Connexion en cours...'
      );
      setUser(user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    }
  };

  const handleRegister = async (name: string, email: string, password: string) => {
    try {
      const user = await withLoading(
        () => RepositoryService.auth.register({
          name,
          email,
          password,
        }),
        'Création du compte...'
      );
      setUser(user);
      setIsAuthenticated(true);
      toast.success('Compte créé avec succès', `Bienvenue ${name.split(' ')[0]} !`);
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await withLoading(
        () => RepositoryService.auth.logout(),
        'Déconnexion...'
      );
      setUser(null);
      setIsAuthenticated(false);
      toast.info('Déconnexion', 'À bientôt !');
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
      throw error;
    }
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!user) throw new Error('Utilisateur non connecté');

    try {
      await RepositoryService.auth.updateProfile(user.id, data);
      setUser(prev => prev ? { ...prev, ...data } : null);
    } catch (error) {
      console.error('Erreur de mise à jour du profil:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await RepositoryService.auth.resetPassword(email);
    } catch (error) {
      console.error('Erreur de réinitialisation du mot de passe:', error);
      throw error;
    }
  };

  const isAdmin = user?.role === 'admin';
  const isZoneManager = user?.role === 'zoneManager';
  const getUserRole = () => {
    return user?.role || 'user';
  };

  return {
    user,
    isAuthenticated,
    isAdmin,
    isZoneManager,
    getUserRole,
    isLoading,
    handleLogin,
    handleRegister,
    handleLogout,
    updateProfile,
    resetPassword,
  };
}